/**
 * The one failure vocabulary every surface speaks: which stream events are the model provider's own
 * error lines, which code a failed turn carries, and the line a user reads for it. No line names the
 * provider, a model, or the provider's wording.
 *
 * The house pool never gives a time: another account may free up first. Only a user's own key says
 * when it resets.
 */
export const TURN_FAILURE_CODES = ['SERVICE_BUSY', 'MODEL_KEY_LIMIT', 'MODEL_KEY_REJECTED', 'MODEL_KEY_BALANCE', 'TURN_FAILED'];
const isRec = (v) => typeof v === 'object' && v !== null;
export function isTurnFailureCode(v) {
    return typeof v === 'string' && TURN_FAILURE_CODES.includes(v);
}
/** Claude Code writes the provider's refusal as a synthetic assistant message and flags it. */
export function isProviderErrorMessage(event) {
    if (!isRec(event) || event.type !== 'assistant')
        return false;
    if (event.is_api_error_message === true)
        return true;
    return isRec(event.message) && event.message.model === '<synthetic>' && typeof event.error === 'string';
}
export function providerErrorText(event) {
    const content = isRec(event) && isRec(event.message) ? event.message.content : undefined;
    if (!Array.isArray(content))
        return '';
    return content.map((b) => (isRec(b) && typeof b.text === 'string' ? b.text : '')).join(' ').trim();
}
function kindFromText(text) {
    if (/hit your .*(limit|budget)|usage limit/i.test(text))
        return 'rate_limit';
    if (/credit balance|billing/i.test(text))
        return 'billing_error';
    if (/\b401\b|api key|oauth|log ?in|authenticat/i.test(text))
        return 'authentication_failed';
    if (/prompt is too long/i.test(text))
        return 'invalid_request';
    if (/overload|\b5\d\d\b|timed out|high load|temporarily/i.test(text))
        return 'server_error';
    return 'unknown';
}
export function providerErrorCode(event, funding) {
    const text = providerErrorText(event);
    const kind = isRec(event) && typeof event.error === 'string' ? event.error : kindFromText(text);
    const own = funding === 'BYOK';
    switch (kind) {
        case 'rate_limit':
            if (/not your usage limit|temporarily limiting/i.test(text))
                return 'SERVICE_BUSY';
            return own ? 'MODEL_KEY_LIMIT' : 'SERVICE_BUSY';
        case 'authentication_failed':
            return own ? 'MODEL_KEY_REJECTED' : 'SERVICE_BUSY';
        case 'billing_error':
            return own ? 'MODEL_KEY_BALANCE' : 'SERVICE_BUSY';
        case 'server_error':
            return 'SERVICE_BUSY';
        default:
            return 'TURN_FAILED';
    }
}
function toDate(at) {
    if (!at)
        return null;
    const d = at instanceof Date ? at : new Date(at);
    return Number.isNaN(d.getTime()) ? null : d;
}
function resetParts(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true, timeZone, timeZoneName: 'short',
    }).formatToParts(at);
    return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}
/** "Mon 26 Oct, 1:00 AM PDT"; "… your time" for the reader's own zone; UTC when the zone is unknown. */
export function formatResetAt(at, timeZone, yourTime = false) {
    const d = toDate(at);
    if (!d)
        return '';
    let p;
    try {
        p = resetParts(d, yourTime ? timeZone ?? undefined : timeZone || 'UTC');
    }
    catch {
        p = resetParts(d, 'UTC');
        yourTime = false;
    }
    const zone = yourTime ? 'your time' : p.timeZoneName;
    return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute} ${p.dayPeriod} ${zone}`;
}
const SAVED = 'Everything made so far is saved.';
export function turnFailureMessage(code, opts = {}) {
    const saved = opts.partway ? SAVED : '';
    const say = (...parts) => parts.filter(Boolean).join(' ');
    switch (code) {
        case 'SERVICE_BUSY':
            return opts.partway
                ? 'We got busy partway through this step. Everything made so far is saved; send a message in a minute to carry on.'
                : "We're busy right now. Send your message again in a minute.";
        case 'MODEL_KEY_LIMIT': {
            const reset = toDate(opts.resetAt);
            const head = reset
                ? `Your model key hit its usage limit; it resets at ${formatResetAt(reset, opts.timeZone, opts.yourTime)}.`
                : 'Your model key hit its usage limit.';
            return say(head, saved, 'Remove it in Settings to use credits instead.');
        }
        case 'MODEL_KEY_REJECTED':
            return say('Your model key was rejected.', saved, 'Update it in Settings, or remove it to use credits.');
        case 'MODEL_KEY_BALANCE':
            return say("Your model key's account is out of balance.", saved, 'Top it up with your provider, or remove the key in Settings to use credits.');
        default:
            return 'Something went wrong on our side and this step stopped. Everything made so far is saved; send your message again to retry.';
    }
}
const PROVIDER_LINES = [
    /^You['’]ve hit your (?:[\w'’]+ )*?(?:limit|budget)(?:$|\s*[·.—])/,
    /^Claude AI usage limit reached\|\d+$/,
    /^(?:Failed to authenticate\. )?API Error: /,
    /^Credit balance is too low/,
    /^Prompt is too long\.?$/,
    /^(?:Invalid API key|OAuth token revoked|Not logged in) · /,
    /^Request timed out\.?$/,
    /^\w+ is experiencing high load, please use \/model/,
    /^Server is temporarily limiting requests/,
    /^Your ANTHROPIC_API_KEY belongs to a disabled organization/,
];
/** A stored row whose whole text is one of the provider's own lines (history saved before the fix). */
export function isProviderErrorLine(text) {
    const t = text.trim();
    return PROVIDER_LINES.some((re) => re.test(t));
}
